import { Fragment, useState, useEffect } from 'react';
import { fetchQueue } from '../../api/relances.api';

const STATUS_LABELS = {
  PENDING: { label: 'En attente', color: 'text-amber-600' },
  CLAIMED: { label: 'En cours', color: 'text-slate-700' },
  RETRY: { label: 'Retry', color: 'text-orange-600' },
  SENT: { label: 'Envoyé', color: 'text-emerald-600' },
  FAILED: { label: 'Échoué', color: 'text-red-600' },
  SKIPPED: { label: 'Ignoré', color: 'text-slate-500' }
};

const CHANNEL_ICONS = {
  EMAIL: '📧',
  WHATSAPP: '📱',
  NONE: '⚠'
};

export default function CommunicationsLog() {
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    channel: '',
    search: '',
    from_date: '',
    to_date: ''
  });
  const [pagination, setPagination] = useState({ page: 1, total: 0, limit: 50 });
  const [expandedId, setExpandedId] = useState(null);

  // Réinitialiser à la page 1 quand les filtres changent
  useEffect(() => {
    setPagination(p => ({ ...p, page: 1 }));
  }, [filters.status, filters.channel, filters.search, filters.from_date, filters.to_date]);

  useEffect(() => {
    loadQueue();
  }, [filters, pagination.page]);

  const loadQueue = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchQueue({
        ...filters,
        page: pagination.page,
        limit: pagination.limit
      });
      setQueue(data.recipients || []);
      setPagination(p => ({ ...p, total: data.total || 0 }));
    } catch (err) {
      console.error('Error loading queue:', err);
      setError(err?.message || 'Erreur lors du chargement des envois');
      setQueue([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const totalPages = Math.max(1, Math.ceil(pagination.total / pagination.limit));

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Envois & Queue</h3>
          <p className="text-sm text-slate-500">
            Statuts unifiés (PENDING/CLAIMED/SENT/FAILED/RETRY) avec erreurs et retries.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <select
          value={filters.status}
          onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
          className="px-3 py-2 border border-slate-200 rounded-lg bg-white text-sm"
        >
          <option value="">Tous statuts</option>
          {Object.keys(STATUS_LABELS).map(k => (
            <option key={k} value={k}>{STATUS_LABELS[k].label}</option>
          ))}
        </select>
        <select
          value={filters.channel}
          onChange={e => setFilters(f => ({ ...f, channel: e.target.value }))}
          className="px-3 py-2 border border-slate-200 rounded-lg bg-white text-sm"
        >
          <option value="">Tous canaux</option>
          <option value="EMAIL">Email</option>
          <option value="WHATSAPP">WhatsApp</option>
          <option value="NONE">Non joignable</option>
        </select>
        <input
          type="text"
          placeholder="Client ou email..."
          value={filters.search}
          onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
          className="px-3 py-2 border border-slate-200 rounded-lg bg-white text-sm w-48"
        />
        <div className="flex items-center gap-1">
          <input
            type="date"
            value={filters.from_date}
            onChange={e => setFilters(f => ({ ...f, from_date: e.target.value }))}
            className="px-2 py-2 border border-slate-200 rounded-lg bg-white text-sm"
            title="Depuis"
          />
          <span className="text-slate-400 text-sm">→</span>
          <input
            type="date"
            value={filters.to_date}
            onChange={e => setFilters(f => ({ ...f, to_date: e.target.value }))}
            className="px-2 py-2 border border-slate-200 rounded-lg bg-white text-sm"
            title="Jusqu'au"
          />
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-slate-500">Chargement...</div>
      ) : queue.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          Aucun envoi trouvé
        </div>
      ) : (
        <div className="overflow-x-auto bg-white border border-slate-200 rounded-lg">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="text-left py-3 px-4 font-medium text-slate-500">Client</th>
                <th className="text-left py-3 px-4 font-medium text-slate-500">Campagne</th>
                <th className="text-center py-3 px-4 font-medium text-slate-500">Canal</th>
                <th className="text-center py-3 px-4 font-medium text-slate-500">Statut</th>
                <th className="text-center py-3 px-4 font-medium text-slate-500">Tentatives</th>
                <th className="text-left py-3 px-4 font-medium text-slate-500">Prochain retry</th>
                <th className="text-left py-3 px-4 font-medium text-slate-500">Erreur</th>
              </tr>
            </thead>
            <tbody>
              {queue.map(row => {
                const isExpanded = expandedId === row.id;
                const { label: statusLabel, color: statusColor } = STATUS_LABELS[row.status] || { label: row.status, color: 'text-slate-600' };
                const channelIcon = CHANNEL_ICONS[row.resolved_channel] || '📤';
                return (
                  <Fragment key={row.id}>
                    <tr
                      className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                      onClick={() => setExpandedId(isExpanded ? null : row.id)}
                    >
                      <td className="py-3 px-4">
                        <div className="font-medium text-slate-900">{row.customer_name || `Client #${row.customer_id}`}</div>
                        <div className="text-xs text-slate-500">{row.customer_email || row.customer_phone || '-'}</div>
                      </td>
                      <td className="py-3 px-4 text-slate-700">{row.campaign_name || '-'}</td>
                      <td className="py-3 px-4 text-center">{channelIcon}</td>
                      <td className="py-3 px-4 text-center">
                        <span className={`font-medium ${statusColor}`}>{statusLabel}</span>
                      </td>
                      <td className="py-3 px-4 text-center">{row.attempts || 0}</td>
                      <td className="py-3 px-4 text-slate-500">{formatDate(row.next_retry_at)}</td>
                      <td className="py-3 px-4 text-slate-500 truncate max-w-xs">{row.error_message || '-'}</td>
                    </tr>
                    {isExpanded && (
                      <tr className="bg-slate-50">
                        <td colSpan="7" className="px-4 py-3">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {row.rendered_subject && (
                              <div>
                                <div className="text-xs font-medium text-slate-500 mb-1">Sujet</div>
                                <div className="text-sm text-slate-900 bg-white p-2 rounded border">
                                  {row.rendered_subject}
                                </div>
                              </div>
                            )}
                            {row.rendered_body && (
                              <div>
                                <div className="text-xs font-medium text-slate-500 mb-1">Email</div>
                                <div className="text-sm text-slate-900 bg-white p-2 rounded border max-h-32 overflow-y-auto">
                                  {row.rendered_body}
                                </div>
                              </div>
                            )}
                            {row.rendered_whatsapp && (
                              <div>
                                <div className="text-xs font-medium text-slate-500 mb-1">WhatsApp</div>
                                <div className="text-sm text-slate-900 bg-white p-2 rounded border">
                                  {row.rendered_whatsapp}
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4 flex justify-between items-center">
        <span className="text-sm text-slate-500">
          {pagination.total > 0 ? `${pagination.total} envoi(s)` : ''}
        </span>
        {totalPages > 1 && (
          <div className="flex gap-2">
            <button
              onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
              disabled={pagination.page === 1}
              className="px-3 py-1 border rounded text-sm disabled:opacity-50"
            >
              Préc.
            </button>
            <span className="px-3 py-1 text-sm">
              {pagination.page} / {totalPages}
            </span>
            <button
              onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
              disabled={pagination.page >= totalPages}
              className="px-3 py-1 border rounded text-sm disabled:opacity-50"
            >
              Suiv.
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
